import React, { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { PushNotifications } from "@capacitor/push-notifications";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { useAuth } from "./AuthContext";
import { useTheme } from "./ThemeContext";
import { useToast } from "./ToastContext";
import { useLanguage, hasChosenLanguage } from "./i18n";
import { api, ApiError } from "./api";
import { RegistrationWizard } from "./components/RegistrationWizard";
import { OwnerApp } from "./components/owner/OwnerApp";
import { TenantApp } from "./components/tenant/TenantApp";
import { Splash } from "./components/Splash";
import { LanguagePicker } from "./components/LanguagePicker";
import { LandingPage } from "./components/LandingPage";
import { initAnalytics, identifyUser, resetAnalytics } from "./analytics";
import { primeCameraPermission } from "./nativePermissions";
import { setPendingNav, parseNavTarget } from "./pendingNav";
import { triggerNotificationPoll } from "./liveNotifications";

// The marketing page only makes sense for a logged-out visitor arriving at
// the bare site — never inside the packaged native app (nobody "browses
// into" an app they already installed), and never when a deep link
// (referral) means they already know what they're here for. Password reset
// is no longer a link-based deep link at all — see RegistrationWizard.tsx's
// forgotPasswordVerify step, which takes an emailed 6-digit code instead of
// a ?resetToken= URL param.
function shouldShowLanding(): boolean {
  if (Capacitor.isNativePlatform()) return false;
  if (new URLSearchParams(window.location.search).get("ref")) return false;
  return true;
}

// Handles the redirect back from Cashfree's DigiLocker consent flow (see
// RentalAgreement.tsx's verifyWithDigilocker) — that's a real external
// navigation, not an in-app one, so this SPA has no way to reopen the exact
// screen the user started from. Landing on Home with a toast is the
// practical outcome here; the RentalAgreement screen itself re-fetches
// verification status fresh every time it's opened, so navigating back
// into it afterward already shows the update without any extra plumbing.
function useDigilockerReturn(loggedIn: boolean) {
  const { refresh } = useAuth();
  const toast = useToast();
  const { t } = useLanguage();
  const loggedInRef = useRef(loggedIn);
  useEffect(() => {
    loggedInRef.current = loggedIn;
  }, [loggedIn]);

  function handle(role: string | null, tenancyId: string | undefined) {
    if (!loggedInRef.current) return;
    if (role !== "owner" && role !== "tenant") return;
    const query = role === "tenant" && tenancyId ? `?tenancyId=${encodeURIComponent(tenancyId)}` : "";
    api
      .get(`/api/digilocker/status${query}`)
      .then((d) => {
        if (d.status === "AUTHENTICATED" && d.verifiedName) {
          toast(t("rentalAgreement.digilockerVerifiedToast", { name: d.verifiedName }));
          if (role === "owner") refresh();
        } else if (d.status === "CONSENT_DENIED") {
          toast(t("rentalAgreement.digilockerDeniedToast"));
        } else if (d.status === "EXPIRED") {
          toast(t("rentalAgreement.digilockerExpiredToast"));
        }
      })
      .catch((err) => {
        toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
      });
  }

  // Web: the redirect is a real page load, so the params sit in the URL bar.
  useEffect(() => {
    if (!loggedIn) return;
    const params = new URLSearchParams(window.location.search);
    const role = params.get("digilocker");
    if (role !== "owner" && role !== "tenant") return;
    const tenancyId = params.get("tenancyId") ?? undefined;

    const url = new URL(window.location.href);
    url.searchParams.delete("digilocker");
    url.searchParams.delete("tenancyId");
    window.history.replaceState({}, "", url.toString());

    handle(role, tenancyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  // Native: DigiLocker's redirect lands in the in-app browser tab (see
  // RentalAgreement.tsx's Browser.open), not back inside this WebView.
  // Android App Links (assetlinks.json + the /digilocker-return intent-filter
  // in AndroidManifest.xml) hand that URL to the already-running app instead,
  // which arrives here as an appUrlOpen event rather than a page load.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const sub = CapacitorApp.addListener("appUrlOpen", (data) => {
      let params: URLSearchParams;
      try {
        params = new URL(data.url).searchParams;
      } catch {
        return;
      }
      handle(params.get("digilocker"), params.get("tenancyId") ?? undefined);
    });
    return () => {
      sub.then((s) => s.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// Feeds pendingNav from every place a "user tapped a notification" signal
// can arrive:
//  - native: @capacitor/push-notifications fires an action-performed event
//    whether the app was already running or this tap is what launched it
//    (Capacitor buffers early events until a listener attaches).
//  - web, app already open in a tab: service-worker.js posts a message to
//    the focused client instead of navigating it directly (a service worker
//    can't reach into the SPA's in-memory view state).
//  - web, no tab was open: service-worker.js opens a new window with
//    ?nav=<target> in the URL, read here once on first load.
function useNotificationNav() {
  useEffect(() => {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios") {
      // iOS registers via @capacitor-firebase/messaging (see
      // NotificationSettings.tsx) instead of @capacitor/push-notifications,
      // so it needs this same tap/foreground wiring against that plugin's
      // own events instead — same idea as the Android branch below, just a
      // different plugin object.
      const tapSub = FirebaseMessaging.addListener("notificationActionPerformed", (action) => {
        const url = (action.notification.data as Record<string, string> | undefined)?.url;
        if (url) setPendingNav(parseNavTarget(url));
      });
      const receivedSub = FirebaseMessaging.addListener("notificationReceived", () => {
        triggerNotificationPoll();
      });
      return () => {
        tapSub.then((s) => s.remove());
        receivedSub.then((s) => s.remove());
      };
    }

    if (Capacitor.isNativePlatform()) {
      const tapSub = PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
        const url = action.notification.data?.url;
        if (url) setPendingNav(parseNavTarget(url));
      });
      // Android suppresses the system-tray heads-up for a push that arrives
      // while the app is already in the foreground — nothing shows up
      // unless the app does something with it itself. This just wakes the
      // already-mounted useLiveNotifications poll (see liveNotifications.ts)
      // so the toast/bell badge react immediately instead of waiting up to
      // POLL_MS, closing the "notifications should come while using the app"
      // gap on native. A tapped notification also fires this event first —
      // harmless, it's just an extra poll alongside the nav handled above.
      const receivedSub = PushNotifications.addListener("pushNotificationReceived", () => {
        triggerNotificationPoll();
      });
      return () => {
        tapSub.then((s) => s.remove());
        receivedSub.then((s) => s.remove());
      };
    }

    if ("serviceWorker" in navigator) {
      const onMessage = (e: MessageEvent) => {
        if (e.data?.type === "notification-nav" && e.data.url) setPendingNav(parseNavTarget(e.data.url));
      };
      navigator.serviceWorker.addEventListener("message", onMessage);
      return () => navigator.serviceWorker.removeEventListener("message", onMessage);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const nav = params.get("nav");
    if (!nav) return;
    const url = new URL(window.location.href);
    url.searchParams.delete("nav");
    window.history.replaceState({}, "", url.toString());
    setPendingNav(parseNavTarget(nav));
  }, []);
}

export function App() {
  const { user, loading } = useAuth();
  const { theme } = useTheme();
  const [showSplash, setShowSplash] = useState(true);
  const [languageChosen, setLanguageChosen] = useState(hasChosenLanguage);
  const [showLanding, setShowLanding] = useState(shouldShowLanding);
  useDigilockerReturn(!!user);
  useNotificationNav();

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    if (user) {
      identifyUser(user.id, user.role, user.city ?? null);
      // Primed only once the user is actually signed in and inside the
      // app — priming at the raw app-mount effect fired the system camera
      // permission dialog before language selection or login, on every
      // fresh install, with zero context for why.
      primeCameraPermission();
    } else {
      resetAnalytics();
    }
  }, [user]);

  if (!loading && !user && showLanding) {
    return <LandingPage onGetStarted={() => setShowLanding(false)} />;
  }

  return (
    <div className="stage">
      <div className="stage-label">ToLetBook &middot; live prototype</div>
      <div className="device" data-theme={theme}>
        <div className="notch" />
        <div className="statusbar">
          <span>9:41</span>
          <span className="icons">📶 🔋</span>
        </div>
        {!languageChosen ? (
          <LanguagePicker onDone={() => setLanguageChosen(true)} />
        ) : loading ? (
          <div className="spinner" />
        ) : !user ? (
          <RegistrationWizard onBackToLanguage={() => setLanguageChosen(false)} />
        ) : user.role === "owner" ? (
          <OwnerApp />
        ) : (
          <TenantApp />
        )}
        {showSplash && <Splash onDone={() => setShowSplash(false)} />}
      </div>
    </div>
  );
}
